/**
 * Abre o onboarding 1s depois do boot:
 *  - "novo" quando o /me traz `aware` (a pessoa ainda não respondeu como conheceu);
 *  - "completar" quando traz `profile` (respondeu o questionário antigo, mas falta segmento ou
 *    alcance), com o que já existe preenchido.
 * Ao concluir o "novo", abre o convite do guia passo a passo no lugar de "Sua primeira busca"
 * (protótipo do Claude Design de 28/10/2026), para quem pode capturar. O "completar" (quem vem da
 * Classic) fecha ao salvar, sem as 3 buscas sugeridas, que são só de quem é novo (decisão do
 * Marcelo, 28/10/2026). O modo fica fixo depois de aberto: o /me volta
 * sem `aware`/`profile` assim que salva, e a janela não pode sumir no meio.
 *
 * Boas-vindas (28/10/2026, pacote "boas-vindas-classic" do Claude Design): no "completar", quem
 * vem da Classic vê antes a apresentação das novidades, e o perfil abre quando ela fecha. A regra
 * é a do próprio "completar" (decisão do Marcelo): `aware` gravado, que só a Classic pedia sem
 * segmento e alcance, e perfil incompleto. Volta a cada acesso até o perfil ser salvo, e para
 * testar de novo basta apagar o segmento ou o alcance da conta. Conta que o /me marca como
 * criada depois da abertura da versão nova (`classic: false`) vai direto ao perfil.
 *
 * Movimento (23/10/2026): o perfil tira a própria janela antes de chamar onDone (o véu fica), e
 * "Sua primeira busca" monta com o véu já aberto; só a janela entra. Ao fechar, usePresenca a
 * mantém montada enquanto véu e janela saem (140 ms).
 */
import { Suspense, lazy, useEffect, useState } from 'react';
import { usePresenca } from '../../hooks/usePresenca';
import { useQueryClient } from '@tanstack/react-query';
import { useSessionStore } from '../../stores/sessionStore';
import type { MeProfile } from '../../types/session';
import { OnboardingDialog, type OnboardingResult } from './OnboardingDialog';
import { FirstSearchDialog } from './FirstSearchDialog';
import { useOnboardingStore } from './onboardingStore';
import { ONBOARDING_ENABLED } from '../../config/env';
import type { OnboardingMode } from './flow';
import { can } from '../../lib/entitlements';
import { useGuiaStore } from '../guia/guiaStore';

const BoasVindas = lazy(() => import('../boasVindas/BoasVindas'));

type Phase =
  | { kind: 'idle' }
  | { kind: 'welcome'; profile?: MeProfile }
  | { kind: 'wizard'; mode: OnboardingMode; profile?: MeProfile }
  | { kind: 'search'; result: OnboardingResult }
  | { kind: 'closed' };

export function OnboardingHost(): JSX.Element | null {
  const session = useSessionStore((s) => s.session);
  const markShown = useOnboardingStore((s) => s.markShown);
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  // A última "Sua primeira busca" aberta, para a janela continuar na tela enquanto sai.
  const [search, setSearch] = useState<Extract<Phase, { kind: 'search' }> | null>(null);
  const searchView = usePresenca(phase.kind === 'search');

  // Chave desligada (config/env): a janela não abre.
  const mode: OnboardingMode | null = !ONBOARDING_ENABLED
    ? null
    : session?.aware
      ? 'novo'
      : session?.profile
        ? 'completar'
        : null;
  const profile = session?.profile;
  const classic = session?.classic;

  useEffect(() => {
    if (phase.kind !== 'idle' || !mode) return;
    const timer = setTimeout(() => {
      markShown();
      setPhase(
        mode === 'completar' && classic !== false
          ? { kind: 'welcome', profile }
          : { kind: 'wizard', mode, profile },
      );
    }, 1000);
    return () => clearTimeout(timer);
  }, [phase.kind, mode, profile, classic, markShown]);

  if (phase.kind === 'welcome') {
    return (
      <Suspense fallback={null}>
        <BoasVindas
          origem="automatica"
          onFechar={() => setPhase({ kind: 'wizard', mode: 'completar', profile: phase.profile })}
        />
      </Suspense>
    );
  }
  if (phase.kind === 'wizard') {
    return (
      <OnboardingDialog
        mode={phase.mode}
        profile={phase.profile}
        onDone={(result) => {
          void qc.invalidateQueries({ queryKey: ['me'] });
          if (phase.mode === 'completar') {
            setPhase({ kind: 'closed' });
            return;
          }
          if (can(useSessionStore.getState().session, 'capture.enabled')) {
            useGuiaStore.getState().abrirConvite(result);
            setPhase({ kind: 'closed' });
            return;
          }
          const next = { kind: 'search', result } as const;
          setSearch(next);
          setPhase(next);
        }}
      />
    );
  }
  if (searchView.montado && search) {
    return (
      <FirstSearchDialog
        profile={search.result}
        aberto={searchView.aberto}
        veuAberto={phase.kind === 'search'}
        onClose={() => setPhase({ kind: 'closed' })}
      />
    );
  }
  return null;
}
